/**
 * RelatedEntitiesSection — registry-primary related entity display.
 *
 * Reads from the connection registry when the trip is in registry_primary/
 * registry_only mode, with legacy content-block fallback during transition.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaMapMarkerAlt, FaHotel, FaPlane, FaCalendarAlt, FaFileAlt, FaTimes } from 'react-icons/fa';
import { getRelatedEntityModels } from '../../services/linkPresentationService';
import { eventBridgeService, EVENT_TYPES } from '../../services/eventService';
import { getDays } from '../../firebase/firestoreService';
import { extractEntityReferencesFromContentBlocks } from '../../utils/contentBlockUtils';
import { formatDayDisplayLabel } from '../../utils/dayDisplayUtils';
import './RelatedEntitiesSection.css';

function getEntityIcon(entityType) {
  switch (entityType) {
  case 'place':
    return <FaMapMarkerAlt className="entity-icon" />;
  case 'accommodation':
    return <FaHotel className="entity-icon" />;
  case 'travel':
    return <FaPlane className="entity-icon" />;
  case 'day':
    return <FaCalendarAlt className="entity-icon" />;
  case 'document':
    return <FaFileAlt className="entity-icon" />;
  default:
    return <FaMapMarkerAlt className="entity-icon" />;
  }
}

function getEntityRoute(tripId, entityType, entityId) {
  switch (entityType) {
  case 'place':
    return `/trips/${tripId}/places/${entityId}`;
  case 'accommodation':
    return `/trips/${tripId}/accommodation/${entityId}`;
  case 'travel':
    return `/trips/${tripId}/travel/${entityId}`;
  case 'day':
    return `/trips/${tripId}/day/${entityId}`;
  case 'document':
    return `/trips/${tripId}/documents/${entityId}`;
  default:
    return '#';
  }
}

const LINK_REFRESH_EVENTS = [
  EVENT_TYPES.LINK_CREATED,
  EVENT_TYPES.LINK_UPDATED,
  EVENT_TYPES.LINK_REMOVED
];

const RelatedEntitiesSection = React.memo(({
  contentBlocks,
  tripId,
  userId,
  hostEntityType,
  hostEntityId,
  className = '',
  hideTitle = false,
  excludeTypes = [],
  onUnlink
}) => {
  const [registryModels, setRegistryModels] = useState([]);
  const [readSource, setReadSource] = useState('legacy');
  const [registryLoadVersion, setRegistryLoadVersion] = useState(0);
  const [dayLabelById, setDayLabelById] = useState({});

  const canReadRegistry = Boolean(userId && tripId && hostEntityType && hostEntityId);
  const excludeTypesKey = excludeTypes.join(',');

  const loadRegistryModels = useCallback(async () => {
    if (!canReadRegistry) {
      setRegistryModels([]);
      setReadSource('legacy');
      return;
    }

    const resolvedExcludeTypes = excludeTypesKey ? excludeTypesKey.split(',') : [];

    try {
      const result = await getRelatedEntityModels(
        userId,
        tripId,
        hostEntityType,
        hostEntityId,
        { surface: 'related', excludeTypes: resolvedExcludeTypes }
      );
      setReadSource(result.source);
      setRegistryModels(result.models || []);
    } catch (error) {
      console.error('[RelatedEntitiesSection] Failed to load registry links:', error);
      setReadSource('legacy');
      setRegistryModels([]);
    }
  }, [canReadRegistry, userId, tripId, hostEntityType, hostEntityId, excludeTypesKey]);

  useEffect(() => {
    loadRegistryModels();
  }, [loadRegistryModels, registryLoadVersion]);

  useEffect(() => {
    if (!canReadRegistry) {
      return undefined;
    }

    const subscriptions = LINK_REFRESH_EVENTS.map((eventType) =>
      eventBridgeService.subscribe(eventType, (payload) => {
        if (payload?.tripId && payload.tripId !== tripId) {
          return;
        }
        setRegistryLoadVersion((version) => version + 1);
      })
    );

    return () => {
      subscriptions.forEach((subscription) => subscription.unsubscribe());
    };
  }, [canReadRegistry, tripId]);

  const resolvedExcludeTypes = useMemo(
    () => (excludeTypesKey ? excludeTypesKey.split(',') : []),
    [excludeTypesKey]
  );

  const legacyEntityRefs = useMemo(
    () => extractEntityReferencesFromContentBlocks(contentBlocks)
      .filter((ref) => !resolvedExcludeTypes.includes(ref.entityType)),
    [contentBlocks, resolvedExcludeTypes]
  );

  useEffect(() => {
    const hasDayRefs = legacyEntityRefs.some((ref) => ref.entityType === 'day');
    if (!hasDayRefs || !userId || !tripId) {
      setDayLabelById({});
      return undefined;
    }

    let cancelled = false;
    getDays(userId, tripId)
      .then((days) => {
        if (cancelled) {
          return;
        }
        const labels = {};
        days.forEach((day) => {
          labels[day.id] = formatDayDisplayLabel(day);
        });
        setDayLabelById(labels);
      })
      .catch((error) => {
        console.error('[RelatedEntitiesSection] Failed to load day labels:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [legacyEntityRefs, userId, tripId]);

  const displayItems = useMemo(() => {
    const registryItems = registryModels.map((model) => ({
      key: model.linkId || `${model.entityType}-${model.entityId}`,
      entityType: model.entityType,
      entityId: model.entityId,
      entityName: model.entityName,
      route: model.route || getEntityRoute(tripId, model.entityType, model.entityId),
      badge: model.badge
    }));

    const legacyItems = legacyEntityRefs.map((ref, index) => ({
      key: `${ref.entityType}-${ref.entityId}-${index}`,
      entityType: ref.entityType,
      entityId: ref.entityId,
      entityName: ref.entityType === 'day' && dayLabelById[ref.entityId]
        ? dayLabelById[ref.entityId]
        : ref.entityName,
      route: getEntityRoute(tripId, ref.entityType, ref.entityId),
      badge: null
    }));

    if (readSource === 'registry' && registryItems.length > 0) {
      const seen = new Set(registryItems.map((item) => `${item.entityType}:${item.entityId}`));
      const merged = [...registryItems];
      legacyItems.forEach((item) => {
        const dedupeKey = `${item.entityType}:${item.entityId}`;
        if (!seen.has(dedupeKey)) {
          seen.add(dedupeKey);
          merged.push(item);
        }
      });
      return merged;
    }

    return legacyItems.length > 0 ? legacyItems : registryItems;
  }, [readSource, registryModels, legacyEntityRefs, tripId, dayLabelById]);

  if (!displayItems.length) {
    return null;
  }

  return (
    <div className={`related-entities-section ${className}`}>
      {!hideTitle && <h3>Related Entities</h3>}
      <div className="entity-chips">
        {displayItems.map((item) => (
          <Link
            key={item.key}
            to={item.route}
            className={`entity-chip ${item.entityType}-entity`}
            title={`View ${item.entityName}`}
          >
            {getEntityIcon(item.entityType)}
            <span className="entity-name">{item.entityName}</span>
            {item.badge && <span className="entity-badge">{item.badge}</span>}
            {onUnlink && (
              <button
                type="button"
                className="unlink-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onUnlink(item.entityType, item.entityId, item.entityName);
                }}
                title={`Unlink ${item.entityName}`}
              >
                <FaTimes />
              </button>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
});

RelatedEntitiesSection.displayName = 'RelatedEntitiesSection';

export default RelatedEntitiesSection;
